<template>
  <v-combobox
    :value="comboValue"
    :items="items"
    :loading="loading"
    :label="label"
    :multiple="multiple"
    :chips="multiple"
    :deletable-chips="multiple"
    :disabled="disabled"
    :rules="rules"
    :hint="hint"
    persistent-hint
    clearable
    outlined
    dense
    no-data-text="Список точек недоступен — введите ID"
    @input="onInput"
  >
    <template
      v-if="multiple && items.length"
      #prepend-item
    >
      <v-list-item
        ripple
        @mousedown.prevent
        @click="toggleAll"
      >
        <v-list-item-action>
          <v-icon>{{ selectAllIcon }}</v-icon>
        </v-list-item-action>
        <v-list-item-content>
          <v-list-item-title>Выбрать всё</v-list-item-title>
        </v-list-item-content>
      </v-list-item>
      <v-divider class="mt-2" />
    </template>
  </v-combobox>
</template>

<script>
// Селект точек прохода места. Список грузится из Charon GET places/{placeId}/points;
// если список недоступен, items пуст и combobox работает как свободный ввод.
// При смене placeId список перезагружается, выбранные точки сбрасываются.
import { SkudCommonEndpoint } from '@/views/skud/shared/endpoint_services/skud-common-endpoint-service'

export default {
  name: 'SkudPointSelect',
  props: {
    value: {
      type: [Array, String],
      default: () => []
    },
    placeId: {
      type: [Number, String],
      default: null
    },
    multiple: {
      type: Boolean,
      default: true
    },
    label: {
      type: String,
      default: 'Точки прохода'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    rules: {
      type: Array,
      default: () => []
    },
    hint: {
      type: String,
      default: 'Выберите из списка или введите ID вручную'
    }
  },
  data () {
    return {
      endpoint: null,
      items: [],
      loading: false
    }
  },
  computed: {
    selectedIds () {
      return Array.isArray(this.value) ? this.value.map(String) : []
    },
    allSelected () {
      return this.items.length > 0 && this.items.every(i => this.selectedIds.includes(i.value))
    },
    someSelected () {
      return this.items.some(i => this.selectedIds.includes(i.value))
    },
    selectAllIcon () {
      if (this.allSelected) return 'mdi-close-box'
      if (this.someSelected) return 'mdi-minus-box'
      return 'mdi-checkbox-blank-outline'
    },
    // v-combobox рисует значение как есть, а наружу мы отдаём id —
    // резолвим id обратно в пункт списка, чтобы показывалось имя точки.
    // Ручной ввод (id нет в списке) остаётся как есть.
    comboValue () {
      if (this.multiple) {
        return (Array.isArray(this.value) ? this.value : []).map(v => this.resolvePoint(v))
      }
      return this.value == null || this.value === '' ? this.value : this.resolvePoint(this.value)
    }
  },
  watch: {
    placeId () {
      this.$emit('input', this.multiple ? [] : null)
      this.load()
    }
  },
  mounted () {
    this.endpoint = new SkudCommonEndpoint()
    this.load()
  },
  methods: {
    resolvePoint (v) {
      const it = this.items.find(i => i.value === String(v))
      return it || v
    },
    // v-combobox в Vuetify 2 игнорирует item-value и emit'ит объект целиком — маппим в id.
    onInput (val) {
      const toId = v => (v && typeof v === 'object') ? String(v.value) : v
      this.$emit('input', Array.isArray(val) ? val.map(toId) : toId(val))
    },
    // emit идёт сразу строками id, минуя onInput (он нужен только для объектов из combobox)
    toggleAll () {
      if (this.allSelected) {
        this.$emit('input', [])
      } else {
        // объединяем, чтобы не потерять вручную введённые ID, которых нет в списке
        const all = this.items.map(i => i.value)
        this.$emit('input', Array.from(new Set([...this.selectedIds, ...all])))
      }
    },
    async load () {
      this.items = []
      if (!this.placeId) return
      this.loading = true
      const points = await this.endpoint.getPoints(this.placeId)
      this.loading = false
      if (!points) return
      this.items = points.map(p => {
        const id = String(p.id !== undefined ? p.id : p.point_id)
        return { value: id, text: p.name ? `${p.name} (${id})` : id }
      })
    }
  }
}
</script>
